import React, { useEffect, KeyboardEvent, useState } from 'react';
import { useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
// api + models
import api from '@/api';
import { selectRegistrationStep } from '@/models/account';
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
import { makeSelectEventById } from '@/models/event';
// styles
import styles from './styles.module.scss';
// types
import { IAccountRegistrationEnum } from '@/models/account/types';
// components
import OTPInput from './OTPInput';
import Button from '@/components/ui/buttons/Button';
import FormSection from '@/components/forms/FormSection';
// utils
import { setPrimaryColor } from '@/utils/cssVars';

import { FORM_FIELDS } from './config';
import { Trans, useTranslation } from 'react-i18next';
import { isRegistrationInActive } from '@/utils/helpers';

const OtpForm = ({
  buttonBackgroundColor,
  updateLoginMethod,
  _email = '',
  setEmail,
  setNotificationMode,
  isOtpLogin = true,
  buttonStyle,
  setOtpInitiated,
  setCheckRegistrationExists,
  isSpeakerPage,
  setShowLogoutPromptForNotCheckinUser,
  isSponsorLandingPage,
  isLoginFlowOnly = false,
}) => {
  const dispatch = useDispatch();
  const { eventId } = useParams<{ eventId: string }>();
  const registrationStep = useSelector(selectRegistrationStep);
  const event = useMemoizedSelector(makeSelectEventById, eventId);

  const [isAccountDeactivated, setIsAccountDeactivated] = useState<boolean>(
    false,
  );
  const isRegistrationsEnabled =
    event.type === 'SERIES' || event?.isRegistrationsEnabled;

  const { t } = useTranslation();
  const { backgroundColor, color } = buttonStyle;

  const root = document.documentElement;
  root.addEventListener('mouseover', e => {
    root.style.setProperty('--background', backgroundColor);
    root.style.setProperty('--font-color', color);
  });

  useEffect(() => {
    const { primaryColor, secondaryColor } = event;
    setPrimaryColor(primaryColor, secondaryColor);
  }, [event]);

  const isPasswordReset =
    registrationStep === IAccountRegistrationEnum.RESET_PASSWORD;

  // Set registration step back to initial step
  const goBackButtonHandler = () => {
    const step = isPasswordReset
      ? IAccountRegistrationEnum.ENTER_PASSWORD
      : IAccountRegistrationEnum.LOGIN_METHOD;
    dispatch({
      type: 'account/setRegistrationStep',
      payload: { registrationStep: step },
    });
    updateLoginMethod(undefined);
  };

  // The handler for a click on the back arrow
  const handleBackClick = () => {
    goBackButtonHandler();
  };

  // The handler for the Enter key while focused on back arrow button
  const handleEnterKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      goBackButtonHandler();
    }
  };

  const [otpValue, setOtpValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [seconds, setSeconds] = useState('');
  const [emailId, setEmailId] = useState(_email);
  const [verificationId, setVerificationId] = useState('');
  const [otpRequestCount, setOtpRequestCount] = useState(0);
  const [otpLimitExceeded, setOtpLimitExceeded] = useState(false);
  // countdown timer for otp request
  const startTimer = () => {
    setTimeout(() => {
      const sec = parseInt(seconds, 0);
      if (sec >= 1) {
        const updatedSecond = `0${sec - 1}`.slice(-2);
        setSeconds(updatedSecond);
      }
    }, 1000);
  };

  // Send OTP to an user by email for the first time.
  const initiateOTP = ({ email }) => {
    const formData = {
      eventId,
      email,
      isAttendeeRegistration: isSpeakerPage
        ? false
        : isPasswordReset
        ? false
        : true,
    };
    (async () => {
      try {
        const { status, data } = await api.customOtp.initiateOTP(formData);
        if (status && status === 200) {
          const { otpVerificationId, noOfAttempts } = data;
          setOtpSent(true);
          setOtpInitiated(true);
          setOtpRequestCount(noOfAttempts);
          setVerificationId(otpVerificationId);
          setSeconds('30');
          startTimer();
          return;
        }
      } catch (err) {
        if (
          err?.response?.status === 401 &&
          err?.response?.data === 'INVITE_ONLY_NO_ACCESS'
        ) {
          dispatch({
            type: 'account/setRegistrationStep',
            payload: {
              registrationStep: IAccountRegistrationEnum.NO_ACCESS,
            },
          });
          return;
        }
        if (
          err?.response?.status === 401 &&
          err?.response?.data === 'UnAutorized User'
        ) {
          setNotificationMode(true);
          setIsAccountDeactivated(true);
        } else if (
          err?.response?.status === 406 &&
          err?.response?.data.message === 'NOT_ACCEPTABLE'
        ) {
          if (isLoginFlowOnly || !isRegistrationsEnabled) {
            dispatch({
              type: 'global/addDangerToast',
              payload: {
                description: err?.response?.data.description,
              },
            });
          } else {
            setCheckRegistrationExists(false);
          }
        } else {
          const errMessage = err?.response?.data?.message;
          const errDesc = err?.response?.data?.description;
          if (isRegistrationInActive(errMessage)) {
            dispatch({
              type: 'global/addDangerToast',
              payload: { description: errDesc },
            });
          } else {
            dispatch({
              type: 'global/addDangerToast',
              payload: {
                description: t(
                  'otp_form.failed_to_send_otp',
                  'Failed to send the OTP, Please try again.',
                ),
              },
            });
          }
        }
      }
    })();
  };

  // Verify the otp entered by the user.
  const verifyOTP = () => {
    const formData = {
      eventId: eventId,
      email: emailId,
      verificationId: verificationId,
      otpCode: otpValue,
      isPasswordReset:
        registrationStep === IAccountRegistrationEnum.RESET_PASSWORD
          ? true
          : false,
      isOtpLogin:
        registrationStep === IAccountRegistrationEnum.EMAIL_OTP_LOGIN
          ? true
          : false,
      isAttendeeRegistration: isSpeakerPage ? false : true,
      organizationId: event?.organizationId,
    };
    (async () => {
      try {
        const { status, data } = await api.customOtp.verifyOTP(formData);
        if (status === 200) {
          if (registrationStep === IAccountRegistrationEnum.RESET_PASSWORD) {
            setOtpInitiated(false);
            dispatch({
              type: 'account/setAccountAndRegistrationStep',
              payload: {
                userData: data,
                registrationStep: IAccountRegistrationEnum.RESET_PASSWORD,
              },
            });
            return;
          }
          dispatch({
            type: 'account/setAccountAndRegistrationStep',
            payload: {
              userData: data,
              registrationStep: IAccountRegistrationEnum.COMPLETE,
            },
          });
          return;
        }
      } catch (err) {
        dispatch({
          type: 'global/addDangerToast',
          payload: {
            description: t(
              'otp_form.otp_verification_failed',
              'OTP verification failed, try again',
            ),
          },
        });
      }
    })();
  };

  // Resend OTP to an user by email {max: 3 attempts}
  const resendOTP = () => {
    const formData = {
      verificationId: verificationId,
    };
    (async () => {
      const { status, data } = await api.customOtp.resendOTP(formData);
      if (status === 200) {
        const { noOfAttempts, otpVerificationId } = data;
        if (noOfAttempts === 3) {
          setOtpLimitExceeded(true);
          setOtpRequestCount(noOfAttempts);
        } else {
          setSeconds('30');
          setOtpLimitExceeded(false);
          setOtpRequestCount(noOfAttempts);
        }
        setVerificationId(otpVerificationId);
      }
    })();
  };

  // Take the user back to email input screen.
  const retryLogin = () => {
    setOtpRequestCount(0);
    setOtpValue('');
    setOtpSent(false);
    setOtpInitiated(false);
    setVerificationId('');
    setEmailId('');
    setOtpLimitExceeded(false);
  };

  const handleFormSubmit = _data => {
    const { email } = _data;

    api.account.checkExistsByEmail_v2(email).then(response => {
      const accountExists: boolean =
        response.data === 'true' || response.data === true;

      if (accountExists) {
        api.eventRole.checkInviteOnlyAccess(email, eventId).then(res => {
          if (res.data === 'false' || res.data === false) {
            if (!isSpeakerPage) {
              const registrationRequestObject = {
                eventId,
                email: email,
              };
              api.registration
                .getRegistrationByEmail(registrationRequestObject)
                .then(res => {})
                .catch(err => {
                  if (!isRegistrationsEnabled) {
                    dispatch({
                      type: 'global/addDangerToast',
                      payload: {
                        description: err?.response?.data.description,
                      },
                    });
                    return;
                  }
                  if (
                    err?.response?.status === 406 &&
                    err?.response?.data.message === 'NOT_ACCEPTABLE' &&
                    !event?.isFlex
                  ) {
                    setCheckRegistrationExists(false);
                    return;
                  }
                  if (
                    err?.response?.status === 406 &&
                    err?.response?.data.message === 'NOT_ACCEPTABLE' &&
                    event?.isFlex
                  ) {
                    dispatch({
                      type: 'global/addDangerToast',
                      payload: {
                        description: err?.response?.data.description,
                      },
                    });
                    return;
                  }
                });
            } else {
              setShowLogoutPromptForNotCheckinUser(true);
              return;
            }
          }
        });
      } else {
        if (isSpeakerPage) {
          setShowLogoutPromptForNotCheckinUser(true);
          return;
        }
      }
    });

    setEmailId(email);
    setEmail(email);
    initiateOTP(_data);
  };

  useEffect(() => {
    startTimer();
  }, [seconds]);

  return (
    <div className={isLoginFlowOnly ? styles.loginFlowContainer : undefined}>
      {!otpSent ? (
        <>
          {isLoginFlowOnly && (
            <p className={styles.descMsg}>
              {t(
                'otp_form.will_send_code',
                'We will send a 6 digit verification code to your email address',
              )}
            </p>
          )}
          <Form
            onSubmit={handleFormSubmit}
            initialValues={{ email: emailId }}
            render={({ handleSubmit, submitting }) => (
              <div className={styles.emailWrapper}>
                <FormSection
                  formData={FORM_FIELDS}
                  isSponsorLandingPage={isSponsorLandingPage}
                />
                {isAccountDeactivated && (
                  <div className={styles.attendeeDeletionMessage}>
                    {`${t(
                      'account.removed_from_event',
                      'You have been removed from the event.',
                    )} ${t(
                      'account.please_contact_organiser',
                      'Please contact the organiser to resolve the issue.',
                    )}`}
                  </div>
                )}
                <Button
                  label={
                    isOtpLogin
                      ? `-> ${t('otp_form.continue', 'Continue')}`
                      : t('otp_form.submit', 'Submit')
                  }
                  type="button"
                  className={styles.sendOtpButton}
                  onClick={handleSubmit}
                  disabled={submitting}
                  style={{ backgroundColor: buttonBackgroundColor }}
                />
              </div>
            )}
          />
        </>
      ) : (
        <div className={styles.otpGroup}>
          {isLoginFlowOnly ? (
            <p className={styles.descMsg}>
              <Trans
                i18nKey="otp_form.enter_verification_code"
                defaults="Please enter the 6 digit verification code sent to <0>{emailId}</0>"
                components={[<span>dummy</span>]}
                values={{ emailId }}
              />
            </p>
          ) : (
            <p className={styles.helperMsg}>
              <Trans
                i18nKey="otp_form.sent_an_otp"
                defaults="We’ve sent an OTP to <0>{emailId}</0>"
                components={[<span>dummy</span>]}
                values={{ emailId }}
              />
            </p>
          )}
          <OTPInput
            autoFocus
            isNumberInput
            length={6}
            className={styles.otpWrapper}
            inputClassName={styles.otpInput}
            onChangeOTP={otp => {
              setOtpValue(otp);
            }}
          />
          {seconds !== '00' && otpRequestCount <= 3 && (
            <p className={styles.countDownTimer}>
              {isLoginFlowOnly && (
                <Trans
                  i18nKey="otp_form.resend_verification_code_in"
                  defaults="Resend verification code in <0>00:{seconds}</0>"
                  components={[<span>dummy</span>]}
                  values={{ seconds }}
                />
              )}
              {!isLoginFlowOnly && (
                <Trans
                  i18nKey="otp_form.resend_otp_in"
                  defaults="Resend OTP in <0>00:{seconds}</0>"
                  components={[<span>dummy</span>]}
                  values={{ seconds }}
                />
              )}
            </p>
          )}
          {seconds === '00' && otpRequestCount < 3 && (
            <Button
              label={
                isLoginFlowOnly
                  ? t(
                      'otp_form.resend_verification_code',
                      'Resend verification code?',
                    )
                  : t('otp_form.resend_otp', 'Resend OTP?')
              }
              type="button"
              className={styles.inlineButton}
              onClick={resendOTP}
            />
          )}
          {otpLimitExceeded && (
            <p className={styles.exceededErr}>
              {isLoginFlowOnly &&
                t(
                  'otp_form.verification_code_limit_exceeded',
                  'Verification code requests exceeded limit.',
                )}
              {!isLoginFlowOnly &&
                t(
                  'otp_form.otp_limit_exceeded',
                  'OTP requests exceeded limit.',
                )}
              <Button
                label="Retry"
                type="button"
                className={styles.inlineButton}
                onClick={retryLogin}
              />
            </p>
          )}
          <Button
            label={
              isLoginFlowOnly
                ? t('otp_form.verify_and_login', 'Verify & login')
                : `-> ${t('otp_form.continue', 'Continue')}`
            }
            type="button"
            className={styles.verifyOtpButton}
            onClick={verifyOTP}
            disabled={emailId.length < 1}
            style={{ backgroundColor: buttonBackgroundColor }}
          />
        </div>
      )}
    </div>
  );
};
export default OtpForm;
